import { UserService } from 'src/app/services/user.service';
import { RequestOptions } from './../../../services/base.service';
import { StatusMessageResultDto } from './../../general/project.service';
import { SelectStatementColumn } from './../../../models/select-statement-column.model';
import { Supplier } from './../../../models/ordering/supplier.model';
import { Location } from '@angular/common';
import { SelectionSetId } from './../../../enums/selection-set-id.enum';
import { PopupControl } from './../../../models/popup-control.model';
import { BaseSelectStatementId } from './../../../enums/base-select-statement-id.enum';
import { CoreService } from './../../../services/core.service';
import { SelectStatementResult } from './../../../models/select-statement-result.model';
import { OrderLine } from './../../../models/ordering/order-line.model';
import { Component, ElementRef, OnInit, ViewChild, inject } from '@angular/core';
import { ActivatedRoute, Data } from '@angular/router';
import { DxDataGridComponent, DxPivotGridComponent, DxChartComponent } from 'devextreme-angular';
import { Order } from 'src/app/models/ordering/order.model';
import { LoadingIndicationService } from 'src/app/services/loading-indication.service';
import { NotificationService } from 'src/app/services/notification.service';
import { ShareDataService } from 'src/app/services/share-data.service';
import { UtilsService } from 'src/app/services/utils.service';
import { HelpService } from '../../help/help.service';
import { SettingsService } from '../../settings/settings.service';
import { TaskService } from '../../task/task.service';
import { MRPService } from '../mrp.service';
import { Field } from 'devextreme/ui/pivot_grid/data_source';
import { firstValueFrom, map } from 'rxjs';
import { NavigateAssociates } from 'src/app/helpers/navigate-associates';
import { TableDataOperator } from 'src/app/operators/table-data.operator';
import { SettingsPopUpService } from 'src/app/settings-pop-up.service';
import { SelectStatementOptions } from 'src/app/models/select-statement-options.model';
import { DxDataGridOperator } from 'src/app/operators/dx-data-grid.operator';
import { ReplenishmentTableComponent } from 'src/app/widgets/replenishment-table/replenishment-table.component';
import { ContentReadyEvent } from 'devextreme/ui/data_grid_types';
import { BookmarkNewComponent } from 'src/app/widgets/bookmark-new/bookmark-new.component';
import { BookmarkElementsOperator } from 'src/app/widgets/bookmark-new/operators/elements-operators/bookmark-elements.operator';
import { DxDataGridBookmarkElementsOperator } from 'src/app/widgets/bookmark-new/operators/elements-operators/dx-data-grid-bookmark-elements.operator';

@Component({
	selector: 'app-order-optimizer',
	templateUrl: './order-optimizer.component.html',
	styleUrls: ['./order-optimizer.component.scss'],
	standalone: false
})
export class OrderOptimizerComponent implements OnInit {

	// <General>
	private readonly _orderIdQueryParamName = "id";
	// </General>

	@ViewChild('bookmarksGroup', { static: false })
	public bookmarksGroup: BookmarkNewComponent;

	public bookmarkElementsOperators: BookmarkElementsOperator[];

	private _activatedRoute: ActivatedRoute = inject(ActivatedRoute);

	public async initializeBookmarks(): Promise<Data> {
		let routeData: Data = await firstValueFrom(this._activatedRoute.data);

		this.bookmarkElementsOperators = [
			new DxDataGridBookmarkElementsOperator(
				UtilsService.computeRouteHash(routeData.id, DxDataGridBookmarkElementsOperator.DEFAULT_ID_PART).toString(),
				() => this._selectedOrderLinesDataGrid
			),
		];

		return routeData;
	}

	@ViewChild("orderTargetsChart", { static: false }) public orderTargetsChart: DxChartComponent;

	private _chartMaxValueColor: string = '#ff7c7c';
	private _chartAverageValueColor: string = '#44546A';
	private _chartLowValueColor: string = '#FFC000';

	public initializeSettings(): void {
		this._settingsService.getCoreSetting(6533, null, true).then(
			(response) => {

				response.onValueChanged.subscribe(
					{
						next: (coreSetting) => {
							this._chartMaxValueColor = coreSetting.value;

							this.orderTargetsChart.instance.refresh();
						}
					}
				);
			}
		);

		this._settingsService.getCoreSetting(6534, null, true).then(
			(response) => {

				response.onValueChanged.subscribe(
					{
						next: (coreSetting) => {
							this._chartAverageValueColor = coreSetting.value;

							this.orderTargetsChart.instance.refresh();
						}
					}
				);
			}
		);

		this._settingsService.getCoreSetting(6535, null, true).then(
			(response) => {

				response.onValueChanged.subscribe(
					{
						next: (coreSetting) => {
							this._chartLowValueColor = coreSetting.value;

							this.orderTargetsChart.instance.refresh();
						}
					}
				);
			}
		);
	}

	public popupControl: PopupControl = new PopupControl(
		{
			title: 'Order List',

			width: '90%',

			height: '90%',
			maxHeight: '90%',
		}
	);
	public addOrderPopupControl: PopupControl = new PopupControl(
		{
			title: 'Add New Order',

			minWidth: 350,
			width: '90%',

			height: '90%',
			maxHeight: '90%',
		}
	);

	private _orderLinesGrandSummaryData: Map<string, { totalValue: number, targetValue: number }> = new Map<string, { totalValue: number, targetValue: number }>();

	private _orderMeasureColumns: SelectStatementColumn[] = [];

	public chartData: { column: string, value: number }[] = [];

	public initializeChartData(): void {
		this.chartData = [];

		for (let a = 0; a < this._orderMeasureColumns.length; a++) {
			let value: number = 0;

			if (this._orderLinesGrandSummaryData.has(this._orderMeasureColumns[a].columnName)) {
				const summaryData = this._orderLinesGrandSummaryData.get(this._orderMeasureColumns[a].columnName);

				value = Math.round((summaryData.totalValue / summaryData.targetValue) * 100);
			}

			this.chartData.push(
				{
					column: this._orderMeasureColumns[a].caption,
					value: value
				}
			);
		}
	}

	public target = 120;
	public lowAverage = 80;

	public customizePoint = (arg: any) => {
		if (arg.value > this.lowAverage && arg.value < this.target) {
			return { color: this._chartAverageValueColor, hoverStyle: { color: this._chartAverageValueColor } };
		}

		if (arg.value > this.target) {
			return { color: this._chartMaxValueColor, hoverStyle: { color: this._chartMaxValueColor } };
		}

		if (arg.value < this.lowAverage) {
			return { color: this._chartLowValueColor, hoverStyle: { color: this._chartLowValueColor } };
		}

		return undefined;
	};

	public customizeLabel = (arg: any) => {
		if (arg.value > this.lowAverage && arg.value < this.target) {
			return {
				visible: true,
				backgroundColor: this._chartAverageValueColor,
				customizeText(e: any) {
					return `${e.valueText}&#37`;
				},
			};
		}

		if (arg.value > this.target) {
			return {
				visible: true,
				backgroundColor: this._chartMaxValueColor,
				customizeText(e: any) {
					return `${e.valueText}&#37`;
				},
			};
		}

		if (arg.value < this.lowAverage) {
			return {
				visible: true,
				backgroundColor: this._chartLowValueColor,
				customizeText(e: any) {
					return `${e.valueText}&#37`;
				},
			};
		}

		return undefined;
	};

	public customizeText = (arg: any) => `${arg.valueText}&#37`;

	public pivotGridData: { fields: Field[], store: any[] } = {
		fields: [
			{
				caption: 'Order Measure',
				dataField: 'row',
				area: 'row',

				sortingMethod: (a, b) => 0
			},
			{
				caption: "Column",
				dataField: 'column',
				area: 'column',

				sortingMethod: (a, b) => {

					return 0;
				}
			},
			{
				caption: 'Sales',
				dataField: 'amount',
				dataType: 'number',
				summaryType: 'sum',
				sortBy: 'none',
				format: '#',
				area: 'data',

				customizeText: (e) => {
					return e.valueText;
				}
			}
		],
		store: [],
	};

	@ViewChild("pivotGrid", { static: false }) public pivotGrid: DxPivotGridComponent;

	public initializePivotGridData(): void {
		this.pivotGridData.store = [];

		for (let a = 0; a < this.selectedOrderLines.length; a++) {
			const orderLine: OrderLine = this.selectedOrderLines[a];

			for (let b = 0; b < this._orderMeasureColumns.length; b++) {
				const column = this._orderMeasureColumns[b];

				const orderMeasureValue = orderLine[column.columnName] ?? 0;
				const maxOrderMeasureValue = this.selectedSupplier[`Max${column.columnName}`]

				this.pivotGridData.store.push(
					{
						row: column.caption,
						esoObjectId: orderLine.EsoObjectId,
						column: "Order",
						amount: orderMeasureValue,
					},
					{
						row: column.caption,
						esoObjectId: orderLine.EsoObjectId,
						column: "Target",
						amount: a === 0 ? maxOrderMeasureValue ?? 0 : 0,
					},
					{
						row: column.caption,
						esoObjectId: orderLine.EsoObjectId,
						column: "(Order / Target) 100&#37",
						amount: Math.round((orderMeasureValue / (this._orderLinesGrandSummaryData.get(column.columnName).targetValue ?? 0)) * 100),
					}
				);
			}
		}

		this.pivotGridData = {
			fields: this.pivotGridData.fields,
			store: this.pivotGridData.store
		};
	}

	public onContentReady(evt: ContentReadyEvent) {
		this.dxDataGridOperator.updateRowCount(evt.component);
	}

	public onCellPrepared(evt: any): void {
		if (evt.area == "data") {
			if (evt.cell.columnPath[0] === '(Order / Target) 100&#37' && evt.cell.rowType == "D") {
				evt.cellElement.firstChild.innerHTML += "%";
			}
		}

		if (evt.area === 'row' && evt.columnIndex === 0) {
			evt.cellElement.style.backgroundColor = 'rgb(245 245 245 / var(--tw-bg-opacity, 1))';
		  }
		  if (evt.area === 'column' && evt.rowIndex === 0) {
			evt.cellElement.style.backgroundColor = 'rgb(245 245 245 / var(--tw-bg-opacity, 1))';
		  }
	}

	onDataGridCellPrepared(e) {
		e.cellElement.style.padding = '5px';
	}

	onToolbarPreparingData(e: any) {
		e.toolbarOptions.items.unshift({
			location: "before",
			template: "columnMode",
		}, {
			location: 'before',
			template: 'heightButtonTemplate'
		});
	}

	compactMode = false;
	onToggleColumnsMode() {
		this.compactMode = !this.compactMode;
	}

	public calculateOrderLinesGrandSummaryData(): void {

		try {
			this._orderLinesGrandSummaryData = new Map<string, { totalValue: number, targetValue: number }>();

			// Calculate summary values.
			for (let a = 0; a < this._orderMeasureColumns.length; a++) {
				const column = this._orderMeasureColumns[a];
				const maxOrderMeasureValue = this.selectedSupplier[`Max${column.columnName}`];

				let summaryData: {
					totalValue: number,
					targetValue: number
				};

				if (this._orderLinesGrandSummaryData.has(column.columnName)) {
					summaryData = this._orderLinesGrandSummaryData.get(column.columnName);
				} else {
					summaryData = {
						totalValue: 0,
						targetValue: maxOrderMeasureValue ?? 0
					};

					this._orderLinesGrandSummaryData.set(
						column.columnName,
						summaryData
					);
				}

				for (let b = 0; b < this.selectedOrderLines.length; b++) {
					const orderLine: OrderLine = this.selectedOrderLines[b];

					const orderMeasureValue = orderLine[column.columnName] ?? 0;

					summaryData.totalValue += orderMeasureValue;
				}
			}
		} catch (e) {
			console.error("calculateOrderLinesGrandSummaryData", e);
		}
	}

	public suppliersSelectStatementResult: SelectStatementResult;

	private _suppliersInitializationPromise: Promise<SelectStatementResult>;

	public async initializeSuppliers(): Promise<SelectStatementResult> {
		const $this = this;

		const selectStatementObservable$ = await this._coreService.getSelectStatementData(
			new SelectStatementOptions(
				{
					baseSelectStatementId: BaseSelectStatementId.OrderOptimizerSupplierData,
					selectionSetId: SelectionSetId.OrderOptimizerSupplierData
				}
			),
			{
				name: "initializeSuppliers",
				showLoadingIndicator: true,
				responseLoggingEnabled: false
			}
		);

		let promise = firstValueFrom(selectStatementObservable$);

		promise.then(
			(response) => {
				$this.suppliersSelectStatementResult = response;
			}
		);

		return promise;
	}


	public orderStatusesTableDataOperator: TableDataOperator = new TableDataOperator(
		new SelectStatementOptions(
			{
				baseSelectStatementId: BaseSelectStatementId.OrderStatusData
			}
		)
	);

	public saveOrderStatus(evt: any): void {

		if (this.selectedOrderUpdateAllowed) {
			this.mrpService.updateOrder(
				this.selectedOrder,
				{
					name: 'saveOrderStatus -> updateOrder:',
					showLoadingIndicator: true,
					responseLoggingEnabled: false
				}
			).subscribe();
		}

		this.selectedOrderUpdateAllowed = true;
	}

	public autocompleteItems: string[] = [];

	public ordersColumns: { columnName: string; caption: string }[] = [];
	public ordersData: any[] = [];

	public dateIdToDateLookupTable: { dateId: number, date: Date }[] = [];

	public async initializeOrders(): Promise<SelectStatementResult> {
		console.log("initializeOrders");

		const $this = this;

		const selectStatementObservable$ = await this._coreService.getSelectStatementData(
			new SelectStatementOptions(
				{
					baseSelectStatementId: BaseSelectStatementId.OrderOptimizerOrderData,
					selectionSetId: SelectionSetId.OrderOptimizerOrderData
				}
			),
			{
				name: "initializeOrders",
				showLoadingIndicator: true,
				responseLoggingEnabled: false
			}
		);

		let promise = firstValueFrom(selectStatementObservable$);

		promise.then(
			(response) => {
				$this.ordersColumns = response.columns;
				$this.ordersData = response.data;

				$this.dateIdToDateLookupTable = [];

				for (let a = 0; a < $this.ordersData.length; a++) {
					const orderData = $this.ordersData[a];

					$this.dateIdToDateLookupTable.push(
						{
							dateId: orderData.CreatedOnDateId,
							date: UtilsService.convertDateIdToDate(orderData.CreatedOnDateId)
						}
					);
				}

				$this.route.queryParamMap.subscribe(
					async (queryParams) => {
						let id: number = +queryParams.get($this._orderIdQueryParamName);

						if (!id && $this.ordersData.length > 0) {
							id = $this.ordersData[0].OrderOrderId;
						}



						await $this.selectOrder(id);

						setTimeout(() => {
							this.bookmarksGroup.applyDefaultBookmark();
						}, 0);
					}
				);

				$this.autocompleteItems = $this.ordersData.map((order: Order) => order.OrderOrderId.toString());

				$this.autocompleteInitialized = true;
			}
		);

		return promise;
	}

	public addOrder(orderDays: number): void {

		let isSupplierId = this.replenishmentTable.pageFiltersOperator.overviewFiltersInputs.find(
			(element) => element.selectionName === 'SupplierId'
		)
		if (isSupplierId == undefined || isSupplierId == null || isSupplierId.selectedValues.length == 0) {
			this._notificationService.error("Please select a supplier first.");
			this.addOrderPopupControl.toggleVisibility()
			return;
		}

		if (isSupplierId.selectedValues.length == 2) {
			this._notificationService.error("Please select only one supplier.");
			this.addOrderPopupControl.toggleVisibility()
			return;
		}

		let isLocationId = this.replenishmentTable.pageFiltersOperator.overviewFiltersInputs.find(
			(element) => element.selectionName === 'Location' || element.displayName === 'Location'
		)

		if (isLocationId == undefined || isLocationId == null || isLocationId.selectedValues.length == 0) {
			this._notificationService.error("Please select a Location first.");
			this.addOrderPopupControl.toggleVisibility()
			return;
		}

		if (isLocationId.selectedValues.length == 2) {
			this._notificationService.error("Please select only one Location.");
			this.addOrderPopupControl.toggleVisibility()
			return;
		}


		const insertOrderObservable$ = this.mrpService.insertOrder(
			new Order(
				0,
				0,
				this.replenishmentTable.pageFiltersOperator.overviewFiltersInputs.find(
					(element) => element.selectionName === 'EsoObjectGroup'
				).selectedValues[0]
			)
		);

		insertOrderObservable$.subscribe(
			async (response) => {
				console.log('insertOrder - response:', response);

				let insertOrderLinesPromise = this.insertOrderLines(response.id, orderDays);

				await this.initializeOrders();
				await insertOrderLinesPromise;
				this.selectOrder(response.id);
			}
		);
	}

	@ViewChild('replenishmentTable')
	public replenishmentTable!: ReplenishmentTableComponent;

	protected replenishmentTableFiltersSelectionSetId: SelectionSetId = SelectionSetId.OrderOptimizerReplenishmentFilters;

	public onAddOrderButtonClicked(event) {
		this.addOrder(+this.replenishmentTable.periodValue);
	}

	//TODO: Think of a better solution to handle a situation like this.
	private selectedOrderUpdateAllowed: boolean = true;

	private _selectedOrder: Order = new Order(-1, -1, 'DEFAULT');
	public get selectedOrder(): Order {
		return this._selectedOrder;
	}
	public set selectedOrder(value: Order) {
		this._selectedOrder = value;

		this.selectedOrderUpdateAllowed = false;
	}
	public selectedSupplier: Supplier;
	public selectedOrderLines: OrderLine[];

	public async selectOrder(id: number): Promise<void> {


		let order: any = this.ordersData.find((order) => order.OrderOrderId === id);

		if (order) {

			const url = `order-optimizer/deep-dive?${this._orderIdQueryParamName}=${id}`;
			this._location.replaceState(url);

			this.selectedOrder = order;

			this.autocompleteValue = this.selectedOrder.OrderOrderId.toString();

			await this._suppliersInitializationPromise;
			this.selectedSupplier = this.suppliersSelectStatementResult.data.find((supplier) => supplier.Id === this.selectedOrder.OrderSupplierId);
			await this.initializeOrderLines(id);
			//await this._orderLinesInitializationPromise;
			//this.selectedOrderLines = this.orderLinesTableDataOperator.selectStatementResult.data.filter((orderLine) => orderLine.OrderId === id);




			this.initializeChartData();
			this.initializeMasterData(id);

			this._selectedOrderLinesDataGrid.instance.refresh();

			//console.dir(this.pivotGrid.dataSource);
		} else {
			const url = `order-optimizer/deep-dive?${this._orderIdQueryParamName}=${this.selectedOrder.OrderOrderId ?? this.selectedOrder.Id}`;
			this._location.replaceState(url);
		}
	}



	public orderLinesTableDataOperatorFilter: TableDataOperator = new TableDataOperator(
		new SelectStatementOptions(
			{
				baseSelectStatementId: BaseSelectStatementId.OrderOptimizerOrderLineData,
				selectionSetId: SelectionSetId.OrderOptimizerOrderLineData
			}
		),
		undefined,
		{
			getObservable: async (selectStatementOptions: SelectStatementOptions, requestOptions: RequestOptions) => (await this.orderLinesTableDataOperatorFilter.defaultGetObservable(selectStatementOptions, requestOptions)).pipe(
				map(
					(response: SelectStatementResult) => {
						let orderMeasureFieldNameRegExp: RegExp = new RegExp(`^ordermeasure\\d`, 'i');

						this._orderMeasureColumns = this.orderLinesTableDataOperatorFilter.selectStatementResult.columns.filter((column) => orderMeasureFieldNameRegExp.test(column.columnName.toLowerCase()));

						return response;
					}
				)
			),
			load: (selectStatementOptions: SelectStatementOptions, requestOptions: RequestOptions) => this.orderLinesTableDataOperatorFilter.defaultLoad(selectStatementOptions, requestOptions),
		}
	);



	private async insertOrderLines(orderId: number, orderDays: number): Promise<StatusMessageResultDto> {
		const orderLines: OrderLine[] = [];

		this.replenishmentTable.tableValuesDataSource.forEach(
			(element) => {
				orderLines.push(
					new OrderLine(
						{
							orderId: orderId,
							esoObjectId: element.EsoObjectId,
							orderDays: orderDays,
							proposalQuantity: element.InboundProposal
						}
					)
				);
			}
		);

		return firstValueFrom(
			this.mrpService.insertOrderLines(
				orderLines,
				{
					name: "insertOrderLines",
					showLoadingIndicator: true,
					responseLoggingEnabled: false
				}
			)
		);

	}

	public async updateOrderLines(orderLines: OrderLine[]): Promise<StatusMessageResultDto> {
		return firstValueFrom(
			this.mrpService.updateOrderLines(
				orderLines,
				{
					name: "updateOrderLines",
					showLoadingIndicator: true,
					responseLoggingEnabled: false
				}
			)
		);
	}

	public async onUpdateSelectionReplenishmentDataClicked(evt: any): Promise<void> {
		console.log("onUpdateSelectionReplenishmentDataClicked - event:", evt);

		await this.replenishmentTable.getData('reload');

		for (let a = 0; a < this.replenishmentTable.tableValuesDataSource.length; a++) {
			const data = this.replenishmentTable.tableValuesDataSource[a];

			for (let b = 0; b < this.selectedOrderLines.length; b++) {
				const orderLine = this.selectedOrderLines[b];

				if (orderLine.EsoObjectId === data.EsoObjectId) {
					console.error(orderLine.ProposalQuantity, data.InboundProposal);

					orderLine.ProposalQuantity = data.InboundProposal;
					orderLine.FinalQuantity = orderLine.ManualQuantity ?? orderLine.ProposalQuantity;

					orderLine.OrderMeasure1 = orderLine.FinalQuantity * data.OrderMeasurePerUnit1;
					orderLine.OrderMeasure2 = orderLine.FinalQuantity * data.OrderMeasurePerUnit2;
					orderLine.OrderMeasure3 = orderLine.FinalQuantity * data.OrderMeasurePerUnit3;

					break;
				}
			}
		}

		this.selectOrder(this.selectedOrder.OrderOrderId);
	}

	public async onOrderLineDataGridSaved(evt: any): Promise<void> {
		console.log("onOrderLineDataGridSaved - event:", evt);

		await this.updateOrderLines(
			evt.changes.map(
				(element) => element.data as OrderLine
			)
		);

		this._mutatedOrderLineManualQuantityCache.clear();
	}

	public onOrderLineDataGridRowUpdated(evt: any): void {
		console.log("onFocusedOrderLineDataGridRowChanged - e:", evt);
	}

	public intersectionGridSelectionChanged(evt: any): void {
		console.log("intersectionGridSelectionChanged - event:", evt);
	}

	private _previouslyEditedOrderLine: OrderLine | any;
	private _editedOrderLine: OrderLine | any;

	public onOrderLineGridDataEditingStart(evt: any): void {
		console.log("onOrderLineGridDataEditingStart - event:", evt);

		this._previouslyEditedOrderLine = this._editedOrderLine;
		this._editedOrderLine = evt.data;

	}

	private _mutatedOrderLineManualQuantityCache: Map<number, number | null> = new Map<number, number | null>();

	public intersectionGridOnEditingCanceled(evt: any): void {
		console.log("intersectionGridOnEditingCanceled - event:", evt);

		for (let a = 0; a < this.selectedOrderLines.length; a++) {
			const orderLine: OrderLine | any = this.selectedOrderLines[a];

			if (this._mutatedOrderLineManualQuantityCache.has(orderLine.Id ?? orderLine.OrderOrderId)) {
				orderLine.ManualQuantity = this._mutatedOrderLineManualQuantityCache.get(orderLine.Id ?? orderLine.OrderOrderId);
				orderLine.FinalQuantity = orderLine.ManualQuantity ?? orderLine.ProposalQuantity;
				orderLine.OrderMeasure1 = orderLine.FinalQuantity * orderLine.OrderMeasurePerUnit1;
				orderLine.OrderMeasure2 = orderLine.FinalQuantity * orderLine.OrderMeasurePerUnit2;
				orderLine.OrderMeasure3 = orderLine.FinalQuantity * orderLine.OrderMeasurePerUnit3;
			}
		}

		this.calculateOrderLinesGrandSummaryData();

		this.initializePivotGridData();
		this.initializeChartData();
		this._selectedOrderLinesDataGrid.instance.refresh();

		this._mutatedOrderLineManualQuantityCache.clear();
	}

	public onOrderLineGridDataFocusedCellChanged(evt: any): void {
		console.log("onOrderLineGridDataFocusedCellChanged - event:", evt);


	}

	public onOrderLineGridDataSelectionChange(evt: any): void {
		console.log("onOrderLineGridDataSelectionChange - event:", evt);
	}


	@ViewChild('selectedOrderLinesDataGrid', { static: false })
	private _selectedOrderLinesDataGrid: DxDataGridComponent;

	public dxDataGridOperator: DxDataGridOperator = new DxDataGridOperator();

	private _orderLineGridDataEditorPreparedCallCountBeforeMutationDetected: number = 0;

	private _mutationObserver: MutationObserver = new MutationObserver(
		(mutationsList, observer) => {

			let validMutationInputElement: HTMLInputElement = this._orderLineGridDataEditorPreparedCallCountBeforeMutationDetected > 1 ? this._previouslyMutatedInputElement : this._mutatedInputElement;
			let validEditedOrderLine: OrderLine | any = this._orderLineGridDataEditorPreparedCallCountBeforeMutationDetected > 1 ? this._previouslyEditedOrderLine : this._editedOrderLine;

			for (let a = 0; a < this.selectedOrderLines.length; a++) {
				const orderLine: OrderLine | any = this.selectedOrderLines[a];

				if (orderLine.EsoObjectId === validEditedOrderLine.EsoObjectId) {

					if (!this._mutatedOrderLineManualQuantityCache.has(orderLine.Id ?? orderLine.OrderOrderId)) {
						this._mutatedOrderLineManualQuantityCache.set(orderLine.Id ?? orderLine.OrderOrderId, orderLine.ManualQuantity);
					}

					orderLine.ManualQuantity = validMutationInputElement.value ? +validMutationInputElement.value : null;

					orderLine.FinalQuantity = orderLine.ManualQuantity ?? orderLine.ProposalQuantity;

					orderLine.OrderMeasure1 = orderLine.FinalQuantity * orderLine.OrderMeasurePerUnit1;
					orderLine.OrderMeasure2 = orderLine.FinalQuantity * orderLine.OrderMeasurePerUnit2;
					orderLine.OrderMeasure3 = orderLine.FinalQuantity * orderLine.OrderMeasurePerUnit3;

					break;
				}
			}

			this.calculateOrderLinesGrandSummaryData();

			this.initializePivotGridData();
			this.initializeChartData();
			this._selectedOrderLinesDataGrid.instance.refresh();


			this._orderLineGridDataEditorPreparedCallCountBeforeMutationDetected = 0;
		}
	);

	private _previouslyMutatedInputElement: HTMLInputElement;
	private _mutatedInputElement: HTMLInputElement;

	public onOrderLineGridDataEditorPrepared(evt: any): void {

		if (evt.parentType == 'dataRow' && evt.dataField == 'ManualQuantity') {
			//console.log("onOrderLineGridDataEditorPrepared - event:", evt);

			this._orderLineGridDataEditorPreparedCallCountBeforeMutationDetected++;

			let inputElement: HTMLInputElement = evt.editorElement.querySelectorAll('input')[0];

			this._previouslyMutatedInputElement = this._mutatedInputElement;

			this._mutatedInputElement = inputElement;

			this._mutationObserver.observe(evt.editorElement, { characterData: false, childList: true, attributes: false, attributeOldValue: false });
		}
	}

	public openOptimizerDeepDive(productId: string): void {
		this._navigates.navigateTO(`/mrp/deepdive`, `?id=${productId}`);
	}

	public autocompleteInitialized: boolean;
	public autocompleteValue: string;

	public onAutocompleteItemClick(evt: any) {
		console.error('onAutocompleteItemClick - event:', evt);

		this.selectOrder(+this.autocompleteValue);
	}

	constructor(
		private _notificationService: NotificationService,
		private _settingsService: SettingsService,
		public userService: UserService,

		public settingsPopUpService: SettingsPopUpService,

		private mrpService: MRPService, private helpService: HelpService,
		private shareDataService: ShareDataService, private taskService: TaskService,
		private route: ActivatedRoute,

		private _coreService: CoreService,

		private _location: Location,

		private _elementRef: ElementRef,
		private _navigates: NavigateAssociates,
	) { }

	async ngOnInit(): Promise<void> {
		this._suppliersInitializationPromise = this.initializeSuppliers();

		await this.initializeBookmarks();

		this.initializeOrders();
		this.initializeSettings();

		this.orderStatusesTableDataOperator.load({
			name: "initializeOrderStatuses",
			showLoadingIndicator: true,
			responseLoggingEnabled: false
		});
	}

	public initializeMasterData(id: any): void {
		this.initializeOrderMasterData(id);
	}

	public masterDataSelectStatementResult: SelectStatementResult;
	public async initializeOrderMasterData(id: any): Promise<SelectStatementResult> {
		const $this = this;

		const filtersItems = [
			{ field: 'OrderOrderId', operator: 4, value: id }
		];

		const selectStatementObservable$ = await this._coreService.getSelectStatementData(
			new SelectStatementOptions(
				{
					baseSelectStatementId: BaseSelectStatementId.OrderOptimizerMasterData,
					selectionSetId: SelectionSetId.OrderOptimizerMasterData,
					filters: filtersItems.map(item => ({ fieldName: item.field, operation: item.operator, value: item.value })) // Add filters here
				}
			),
			{
				name: "initializeOrderMasterData",
				showLoadingIndicator: true,
				responseLoggingEnabled: false
			}
		);

		let promise = firstValueFrom(selectStatementObservable$);

		promise.then(
			(response) => {
				$this.masterDataSelectStatementResult = { selectionSetId: null, columns: [], data: [] };
				if (response && response.data && response.data.length > 0 && response.data.length < 2) {
					$this.masterDataSelectStatementResult = response;
				}
			}
		);

		return promise;
	}

	/* Order Line loading data filter */
	public async initializeOrderLines(id: any) {
		await this.initializeDataOrderLine(id);
	}

	public OrderLineSelectStatementResult: SelectStatementResult;
	public async initializeDataOrderLine(id: any): Promise<SelectStatementResult> {
		const $this = this;

		const filtersItems = [
			{ field: 'OrderId', operator: 4, value: id }
		];

		const selectStatementObservable$ = await this._coreService.getSelectStatementData(
			new SelectStatementOptions(
				{
					baseSelectStatementId: BaseSelectStatementId.OrderOptimizerOrderLineData,
					selectionSetId: SelectionSetId.OrderOptimizerOrderLineData,
					filters: filtersItems.map(item => ({ fieldName: item.field, operation: item.operator, value: item.value })) // Add filters here
				}
			),
			{
				name: "initializeOrderLine",
				showLoadingIndicator: false,
				responseLoggingEnabled: false
			}
		);

		let promise = firstValueFrom(selectStatementObservable$);

		promise.then(
			(response) => {
				$this.OrderLineSelectStatementResult = { selectionSetId: null, columns: [], data: [] };
				$this.selectedOrderLines = [];
				if (response && response.data && response.data.length > 0) {
					$this.OrderLineSelectStatementResult = response;
					$this.selectedOrderLines = response.data;
				}

				let orderMeasureFieldNameRegExp: RegExp = new RegExp(`^ordermeasure\\d`, 'i');

				this._orderMeasureColumns = this.OrderLineSelectStatementResult.columns.filter((column) => orderMeasureFieldNameRegExp.test(column.columnName.toLowerCase()));


				$this.calculateOrderLinesGrandSummaryData();
				$this.initializePivotGridData();
				$this.calculateOrderLinesGrandSummaryData();
				$this._selectedOrderLinesDataGrid.instance.refresh();
				$this._orderLineGridDataEditorPreparedCallCountBeforeMutationDetected = 0;
			}
		);

		return promise;
	}

}
